import { DirectReportDto, ProductVisibility } from "@coaching-culture/types";
import {
  Button,
  formatDate,
  Label,
  Modal,
  typeSize,
  UserProfileImage,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { useGoalTerminology } from "auth/OrgProvider";
import { differenceInCalendarDays, format, fromUnixTime } from "date-fns";
import { useProductVisibility } from "hooks/useProductVisibility";
import { useState } from "react";
import { IconType } from "react-icons";
import {
  FaBullseye,
  FaCommentDots,
  FaEnvelope,
  FaRegChartBar,
} from "react-icons/fa";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import {
  ActivityEvent,
  ActivityKind,
  ActivityTone,
  conversationHref,
  pastActivity,
  upcomingConversations,
} from "./activity";
import { makeRamp } from "./MeasuresChart";
import { MetricStatus, metricStatusLabels, useTeamRagThresholds } from "./metrics";
import {
  areaDetails,
  cardStatuses,
  daysAhead,
  firstName,
  nextStepFor,
  TeamDrawer,
} from "./nextStep";
import { Strip, StripTag, StripText, StripTitle } from "./NextStepStrip";
import { ReviewMeasuresDrawer } from "./ReviewMeasuresDrawer";
import { ScheduleConversationDrawer } from "./ScheduleConversationDrawer";
import { SendFeedbackDrawer } from "./SendFeedbackDrawer";
import { SetGoalDrawer } from "./SetGoalDrawer";

const PAGE_SIZE = 8;

const Header = styled.header`
  display: flex;
  gap: 18px;
  align-items: center;
  flex-wrap: wrap;
  padding: 24px 64px 20px 28px;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 3px;

  & > h2 {
    margin: 0;
    font-size: ${typeSize("page")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
  }

  & > span {
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/** The drawers slide over this, so it needs to be the positioned parent. */
const Body = styled.div`
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 420px) minmax(0, 1fr);
  min-height: 560px;

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StatusColumn = styled.div`
  padding: 20px 24px 28px;
  border-right: 1px solid ${(p) => p.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 860px) {
    border-right: 0;
    border-bottom: 1px solid ${(p) => p.theme.colors.border};
  }
`;

const StatusList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const StatusRow = styled.li`
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) auto;
  gap: 6px 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey5};

  &:last-child {
    border-bottom: 0;
  }

  & > svg {
    width: 16px;
    height: 16px;
    fill: ${(p) => p.theme.colors.grey};
  }

  & > strong {
    font-size: ${typeSize("body")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
  }

  & > span:first-of-type {
    justify-self: end;
  }
`;

const RowDetail = styled.span`
  grid-column: 2;
  font-size: ${typeSize("small")};
  color: ${(p) => p.theme.colors.textMuted};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowAction = styled.div`
  grid-column: 3;
  justify-self: end;
`;

const ActivityColumn = styled.section`
  padding: 20px 28px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: ${(p) => p.theme.colors.grey7};
  min-width: 0;
`;

const ActivityHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  & > h3 {
    margin: 0;
    flex: 1;
    font-size: ${typeSize("title")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Chip = styled.button<{ active: boolean }>`
  min-height: 32px;
  padding: 0 12px;
  border-radius: 16px;
  font-family: inherit;
  font-size: ${typeSize("caption")};
  font-weight: ${(p) => (p.active ? 600 : 500)};
  cursor: pointer;
  border: 1px solid
    ${(p) => (p.active ? p.theme.colors.black : p.theme.colors.borderStrong)};
  background: ${(p) =>
    p.active ? p.theme.colors.black : p.theme.colors.surface};
  color: ${(p) => (p.active ? p.theme.colors.surface : p.theme.colors.body)};

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const GroupLabel = styled.h4`
  margin: 0;
  font-size: ${typeSize("caption")};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.textMuted};
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const UpcomingItem = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 10px;
`;

const EventList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const EventRow = styled.li`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: start;
  padding: 12px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.grey5};
`;

const EventText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  & > strong,
  & > a {
    font-size: ${typeSize("body")};
    font-weight: 600;
    color: ${(p) => p.theme.colors.black};
  }

  & > a {
    text-decoration: none;
  }

  & > a:hover {
    text-decoration: underline;
  }

  & > span {
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EventDate = styled.span`
  font-size: ${typeSize("caption")};
  color: ${(p) => p.theme.colors.textMuted};
  white-space: nowrap;
  padding-top: 2px;
`;

const toneColours: Record<ActivityTone, { bg: string; fg: string }> = {
  good: { bg: "positiveSoft", fg: "positiveText" },
  warning: { bg: "warningSoft", fg: "warningText" },
  muted: { bg: "grey5", fg: "textMuted" },
  plain: { bg: "surface", fg: "textMuted" },
};

const Dot = styled.span<{ tone: ActivityTone }>`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(p) => p.theme.colors[toneColours[p.tone].bg]};
  border: 1px solid
    ${(p) => (p.tone === "plain" ? p.theme.colors.border : "transparent")};

  & > svg {
    width: 15px;
    height: 15px;
    fill: ${(p) => p.theme.colors[toneColours[p.tone].fg]};
  }
`;

const EmptyNote = styled.div`
  padding: 14px 16px;
  border: 1px dashed ${(p) => p.theme.colors.borderStrong};
  border-radius: 10px;
  font-size: ${typeSize("small")};
  color: ${(p) => p.theme.colors.textMuted};
  line-height: 1.5;
`;

const Bars = styled.ul`
  grid-column: 1 / -1;
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > li {
    display: grid;
    grid-template-columns: 96px minmax(0, 1fr) 92px;
    gap: 10px;
    align-items: center;
    font-size: ${typeSize("caption")};
  }

  & > li > span:first-child {
    color: ${(p) => p.theme.colors.textMuted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & > li > span:last-child {
    font-weight: 600;
    color: ${(p) => p.theme.colors.black};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Track = styled.span`
  display: block;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: ${(p) => p.theme.colors.grey5};

  & > span {
    display: block;
    height: 100%;
    border-radius: 4px;
  }
`;

const kindIcons: Record<ActivityKind, IconType> = {
  conversation: FaCommentDots,
  goal: FaBullseye,
  feedback: FaEnvelope,
  measures: FaRegChartBar,
};

const MeasureBars = ({ user }: { user: DirectReportDto }) => {
  const theme = useTheme() as any;

  return (
    <Bars>
      {user.measures.map((m) => {
        const ramp = makeRamp(
          [theme.colors.danger, theme.colors.warning, theme.colors.positive],
          m.options.length,
        ).reverse();
        const index = m.options.findIndex((o) => o.id === m.value);
        const position = index / Math.max(1, m.options.length - 1);
        const value = index > -1 ? m.options[index].name : "Not set";

        return (
          <li key={m.id}>
            <span title={m.name}>{m.name}</span>
            <Track aria-hidden>
              {index > -1 && (
                <span
                  style={{
                    width: `${10 + position * 90}%`,
                    background: ramp[index],
                  }}
                />
              )}
            </Track>
            <span title={value}>{value}</span>
          </li>
        );
      })}
    </Bars>
  );
};

type AreaProps = {
  icon: IconType;
  title: string;
  status: MetricStatus;
  detail: string;
  action: string;
  onAction: () => void;
  children?: React.ReactNode;
};

const Area = ({
  icon: Icon,
  title,
  status,
  detail,
  action,
  onAction,
  children,
}: AreaProps) => (
  <StatusRow>
    <Icon aria-hidden />
    <strong>{title}</strong>
    <Label size="small" color={status}>
      {metricStatusLabels[status]}
    </Label>
    <RowDetail title={detail}>{detail}</RowDetail>
    <RowAction>
      <Button small onClick={onAction}>
        {action}
      </Button>
    </RowAction>
    {children}
  </StatusRow>
);

type Filter = "all" | ActivityKind;

export type TeamViewModalProps = {
  user: DirectReportDto;
  onClose: () => void;
  /** Open with this drawer already showing. */
  initialDrawer?: TeamDrawer;
  onViewPrivateNotes: () => void;
  onViewDevelopmentNotes: () => void;
};

export const TeamViewModal = ({
  user,
  onClose,
  initialDrawer,
  onViewPrivateNotes,
  onViewDevelopmentNotes,
}: TeamViewModalProps) => {
  const [me] = useUser();
  const terms = useGoalTerminology();
  const history = useHistory();
  const [drawer, setDrawer] = useState<TeamDrawer | "">(initialDrawer ?? "");
  const [filter, setFilter] = useState<Filter>("all");
  const [showAll, setShowAll] = useState(false);
  const hasMeasures =
    useProductVisibility("measures") === ProductVisibility.Enabled;
  const thresholds = useTeamRagThresholds();

  const statuses = cardStatuses(user, thresholds, hasMeasures);
  const step = nextStepFor(user, me.id, statuses, terms);
  const details = areaDetails(user, me.id, terms);
  const first = firstName(user.name);

  const upcoming = upcomingConversations(user).slice(0, 3);
  const events = pastActivity(user, terms).filter(
    (x) => filter === "all" || x.kind === filter,
  );
  const shown = showAll ? events : events.slice(0, PAGE_SIZE);
  const hasFeedback = user.receivedFeedback.length > 0;

  const runStep = () => {
    if (step.href) {
      history.push(step.href);
    } else if (step.drawer) {
      setDrawer(step.drawer);
    }
  };

  const chips: { value: Filter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "conversation", label: "Conversations" },
    { value: "goal", label: terms.goal.asPluralTitle() },
    { value: "feedback", label: "Feedback" },
  ];

  const pickFilter = (f: Filter) => {
    setFilter(f);
    setShowAll(false);
  };

  return (
    <Modal onClose={onClose} showCloseButton width={1000} p={0}>
      <Header>
        <UserProfileImage
          profileImage={user.profileImage}
          name={user.name}
          size={64}
        />
        <HeaderText>
          <h2>{user.name}</h2>
          <span>{user.jobTitle ?? user.email}</span>
        </HeaderText>
        <HeaderActions>
          <Button small onClick={onViewPrivateNotes}>
            Private notes
          </Button>
          <Button small onClick={onViewDevelopmentNotes}>
            Development notes
          </Button>
          <Button small to={`/solutions/my-team/${user.id}`}>
            View profile
          </Button>
        </HeaderActions>
      </Header>
      <Body>
        {drawer === "feedback" && (
          <SendFeedbackDrawer onClose={() => setDrawer("")} user={user} />
        )}
        {drawer === "measures" && (
          <ReviewMeasuresDrawer onClose={() => setDrawer("")} user={user} />
        )}
        {drawer === "goals" && (
          <SetGoalDrawer onClose={() => setDrawer("")} user={user} />
        )}
        {drawer === "conversation" && (
          <ScheduleConversationDrawer
            onClose={() => setDrawer("")}
            user={user}
          />
        )}

        <StatusColumn>
          <Strip tone={step.tone}>
            <StripText>
              <StripTag tone={step.tone}>{step.tag}</StripTag>
              <StripTitle title={step.title}>{step.title}</StripTitle>
            </StripText>
            <Button
              variant={step.tone === "calm" ? "secondary" : "primary"}
              onClick={runStep}
            >
              {step.cta}
            </Button>
          </Strip>

          <StatusList>
            <Area
              icon={FaBullseye}
              title={terms.goal.asPluralTitle()}
              status={statuses.goals}
              detail={details.goals}
              action={`Set ${terms.goal.asTitle()}`}
              onAction={() => setDrawer("goals")}
            />
            <Area
              icon={FaCommentDots}
              title="Conversations"
              status={statuses.conversations}
              detail={details.conversations}
              action="Schedule"
              onAction={() => setDrawer("conversation")}
            />
            <Area
              icon={FaEnvelope}
              title="Feedback"
              status={statuses.feedback}
              detail={details.feedback}
              action="Give feedback"
              onAction={() => setDrawer("feedback")}
            />
            {statuses.measures != null && (
              <Area
                icon={FaRegChartBar}
                title="Measures"
                status={statuses.measures}
                detail={details.measures}
                action="Review"
                onAction={() => setDrawer("measures")}
              >
                {user.measures.length > 0 && <MeasureBars user={user} />}
              </Area>
            )}
          </StatusList>
        </StatusColumn>

        <ActivityColumn aria-labelledby="team-activity">
          <ActivityHeader>
            <h3 id="team-activity">Activity</h3>
            <Chips>
              {chips.map((c) => (
                <Chip
                  key={c.value}
                  type="button"
                  active={filter === c.value}
                  aria-pressed={filter === c.value}
                  onClick={() => pickFilter(c.value)}
                >
                  {c.label}
                </Chip>
              ))}
            </Chips>
          </ActivityHeader>

          {(filter === "all" || filter === "conversation") && (
            <Group>
              <GroupLabel>Upcoming</GroupLabel>
              {upcoming.length === 0 ? (
                <EmptyNote>No conversations booked with {first}.</EmptyNote>
              ) : (
                upcoming.map((c) => (
                  <UpcomingItem key={`${c.id}-${c.index}`}>
                    <Dot tone="warning" aria-hidden>
                      <FaCommentDots />
                    </Dot>
                    <EventText>
                      <strong>{c.type}</strong>
                      <span>
                        {format(
                          fromUnixTime(c.scheduledDate),
                          "EEE d MMM, HH:mm",
                        )}{" "}
                        ·{" "}
                        {daysAhead(
                          differenceInCalendarDays(
                            fromUnixTime(c.scheduledDate),
                            new Date(),
                          ),
                        )}
                      </span>
                    </EventText>
                    <Button small to={conversationHref(c)}>
                      Prepare
                    </Button>
                  </UpcomingItem>
                ))
              )}
            </Group>
          )}

          <Group>
            <GroupLabel>Earlier</GroupLabel>
            {shown.length === 0 ? (
              <EmptyNote>Nothing yet.</EmptyNote>
            ) : (
              <EventList>
                {shown.map((e) => (
                  <ActivityRow key={e.id} event={e} />
                ))}
              </EventList>
            )}
            {!showAll && events.length > PAGE_SIZE && (
              <Button
                variant="ghost"
                small
                onClick={() => setShowAll(true)}
                style={{ alignSelf: "flex-start" }}
              >
                Show {events.length - PAGE_SIZE} more
              </Button>
            )}
          </Group>

          {!hasFeedback && (filter === "all" || filter === "feedback") && (
            <EmptyNote>
              {first} hasn't received any feedback yet. When they do, it will
              show here.
            </EmptyNote>
          )}
        </ActivityColumn>
      </Body>
    </Modal>
  );
};

const ActivityRow = ({ event }: { event: ActivityEvent }) => {
  const Icon = kindIcons[event.kind];
  return (
    <EventRow>
      <Dot tone={event.tone} aria-hidden>
        <Icon />
      </Dot>
      <EventText>
        {event.href ? (
          <Link to={event.href}>{event.title}</Link>
        ) : (
          <strong>{event.title}</strong>
        )}
        {event.detail && <span title={event.detail}>{event.detail}</span>}
      </EventText>
      <EventDate>{formatDate(fromUnixTime(event.date))}</EventDate>
    </EventRow>
  );
};
